import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import PayrollExportCentre from "@/components/PayrollExportCentre";
import PayrollCentre from "@/components/PayrollCentre";
import { XeroPayrollIssuesBanner, XeroTransferDetails } from "@/components/XeroPayrollIssuesBanner";
import { canManagePayroll, canReadXeroPayroll, readPeriodTimesheets } from "@/lib/payrollCentre";
import { useAuth } from "@/lib/auth";
import { localIsoDay } from "@/lib/dates";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, CheckCircle2, Banknote, FileText, Eye, Download, Briefcase } from "lucide-react";
import { toast } from "sonner";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });

const ENTRY_TYPE_LABELS = {
  rained_off: "🌧️ Rained off",
  sick: "🤒 Sick day",
  annual_leave: "🏖️ Annual leave",
  public_holiday: "🇳🇿 Public holiday",
  bereavement: "🕊️ Bereavement",
  other_leave: "📅 Other leave",
};
const WEEK_END_DOW_KEY = "fl_payroll_week_end_dow"; // 0=Sun..6=Sat (JS convention) — local fallback only

// The org-level `pay_week_end_day` uses Python's weekday convention: Mon=0..Sun=6.
// JS Date.getDay() returns Sun=0..Sat=6. Convert between the two.
const pyToJsDow = (py) => ((Number(py) + 1) % 7);
const jsToPyDow = (js) => ((Number(js) + 6) % 7);

function weekBounds(date, endDow = 6) {
  // endDow: which day-of-week is the LAST day of the pay week (Sun=0..Sat=6)
  const d = new Date(date);
  const day = d.getDay(); // 0..6
  // distance forward from `d` to reach endDow (inclusive of today)
  const fwd = (endDow - day + 7) % 7;
  const end = new Date(d); end.setDate(end.getDate() + fwd);
  // If the computed end is in the future, snap back one week so we always
  // show a period ending on/before today.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (end > today) end.setDate(end.getDate() - 7);
  const start = new Date(end); start.setDate(start.getDate() - 6);
  return { week_start: localIsoDay(start), week_end: localIsoDay(end) };
}

function shiftWeek(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return localIsoDay(d);
}

function isoSubtractDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() - n);
  return localIsoDay(d);
}
function prettyDate(iso) {
  return iso ? new Date(iso).toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" }) : "";
}

const xeroTransferMessage = run => ({not_required: 'No eligible hours to send to Xero.', sent: 'Hours sent to Xero.', pending: 'Sending hours to Xero...', sending: 'Sending hours to Xero...', partial: 'Hours sent to Xero for some workers. Others need attention, listed below.', attention: 'Xero needs attention.', off: 'Xero automatic transfer is off, so these hours were not sent.'}[run.xero_transfer?.state] || '');

export default function Payroll() {
  const { user } = useAuth();
  if (!user) return null;
  return <PayrollWorkspace key={`${user.org_id}:${user.id}:${user.role}`} user={user} />;
}

function PayrollWorkspace({ user }) {
  const isOffice = canManagePayroll(user);
  const isAdmin = canReadXeroPayroll(user);
  const weekRequest = useRef(null);
  const confirmationRequest = useRef(null);
  const [loading, setLoading] = useState(isOffice);
  const [loadError, setLoadError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [liveRecords, setRecords] = useState([]);
  const records = liveRecords;
  const [xero, setXero] = useState(null);
  const [xeroConnected, setXeroConnected] = useState(false);
  // Preferred last-day-of-week in JS convention (Sun=0..Sat=6). Source-of-truth is
  // the org record (Python convention Mon=0..Sun=6). Default to Saturday until the
  // org doc loads. localStorage is only used as an instant fallback so the UI
  // doesn't flash to Mon-Sun before the /org/me request returns.
  const [endDow, setEndDow] = useState(() => {
    const v = Number(localStorage.getItem(WEEK_END_DOW_KEY) ?? 6);
    return Number.isInteger(v) && v >= 0 && v <= 6 ? v : 6; // default Saturday
  });
  const [weekStart, setWeekStart] = useState(() => weekBounds(new Date(), Number(localStorage.getItem(WEEK_END_DOW_KEY) ?? 6)).week_start);
  const [liveData, setData] = useState({ week_start: "", week_end: "", workers: [] });
  const data = liveData;
  const [runs, setRuns] = useState([]);
  const [processing, setProcessing] = useState(false);
  const operationStorageKey = `fl_payroll_operation:${user.org_id}:${user.id}`;
  const [pendingOperation, setPendingOperation] = useState(() => sessionStorage.getItem(operationStorageKey));
  const [confirmation, setConfirmation] = useState(null);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [confirmationError, setConfirmationError] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [payslips, setPayslips] = useState([]);
  const [reopenRun, setReopenRun] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);
  // Reopening a processed pay run is a Fieldlink platform action only — ordinary
  // org admins/office can no longer unlock payroll. Shown to a genuine super_admin
  // or a super-admin impersonation session.
  const canReopenPayroll = user.role === "super_admin" || !!user.impersonated_by;

  const reopenPayroll = async () => {
    if (!reopenRun || !reopenReason.trim() || reopening) return;
    setReopening(true);
    try {
      await api.post(`/payruns/${reopenRun.id}/reopen`, { reason: reopenReason.trim() });
      setReopenRun(null);
      setReopenReason("");
      toast.success("Payroll reopened. Recheck and approve the timesheets before processing again.");
      await Promise.all([loadWeek(weekStart), loadRuns()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reopen payroll");
    } finally {
      setReopening(false);
    }
  };

  // On mount, pull the org's pay_week_end_day and align the screen to it.
  // This ensures every office user (and every device) opens Payroll on the
  // SAME pay period as the field workers' "hours this week" tile.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: org } = await api.get("/org/me");
        if (cancelled || !org) return;
        const py = org.pay_week_end_day;
        if (Number.isInteger(py) && py >= 0 && py <= 6) {
          const js = pyToJsDow(py);
          localStorage.setItem(WEEK_END_DOW_KEY, String(js));
          if (js !== endDow) {
            setEndDow(js);
            setWeekStart(weekBounds(new Date(), js).week_start);
          }
        }
      } catch { /* ignore — keep localStorage fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadWeek = async (ws) => {
    if (!isOffice) return;
    weekRequest.current?.abort();
    const controller = new AbortController(); weekRequest.current = controller;
    setLoading(true); setLoadError(''); setRecords([]);
    setData({ week_start: ws, week_end: shiftWeek(ws, 6), workers: [] });
    try {
      const we = shiftWeek(ws, 6);
      const [response, sheets] = await Promise.all([
        api.get('/payroll/week', { params: { week_start: ws, week_end: we }, signal: controller.signal, __flSkipReadCache: true }),
        readPeriodTimesheets(api, ws, we, controller.signal),
      ]);
      if (controller.signal.aborted) return;
      const canonical = new Map((response.data.workers || []).flatMap(w => (w.timesheets || []).map(t => [t.id, t])));
      const approved = sheets.filter(t => t.status === 'approved');
      if (canonical.size !== approved.length || approved.some(t => !canonical.has(t.id) || Number(canonical.get(t.id).hours) !== Number(t.hours) || !!canonical.get(t.id).paid !== !!t.paid)) throw new Error('Incomplete payroll period');
      setData(response.data); setRecords(sheets);
    } catch {
      if (!controller.signal.aborted) setLoadError('The complete payroll period could not be loaded. Refresh before processing or downloading the period PDF.');
    } finally { if (!controller.signal.aborted) setLoading(false); }
  };
  const loadRuns = async () => {
    if (!isOffice) return;
    try { const { data } = await api.get('/payruns', {__flSkipReadCache: true}); setRuns(data); setHistoryError(''); }
    catch { setHistoryError('Pay-run history could not be loaded.'); }
  };
  useEffect(() => {
    if (!runs.some(r => ['pending', 'sending'].includes(r.xero_transfer?.state))) return;
    const timer = setInterval(loadRuns, 5000);
    return () => clearInterval(timer);
  }, [runs]);
  const loadPayslips = async () => {
    if (user.role !== 'field') return;
    try { const { data } = await api.get('/payslips/me'); setPayslips(data); }
    catch { toast.error('Payslips could not be loaded'); }
  };
  useEffect(() => {
    if (isOffice) loadWeek(weekStart);
    else loadPayslips();
    return () => { weekRequest.current?.abort(); confirmationRequest.current?.abort(); };
  }, [weekStart]);
  useEffect(() => { loadRuns(); }, []);
  const loadXero = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data: connection } = await api.get('/xero/status', {__flSkipReadCache:true});
      setXeroConnected(!!connection.connected);
      if (!connection.connected || !connection.payroll_ready) { setXero(null); return; }
      const [mapping, audit] = await Promise.allSettled([api.get('/xero/payroll/mappings', {__flSkipReadCache:true}), api.get('/xero/payroll/audit', {__flSkipReadCache:true})]);
      setXero({ tenant: connection.tenant_name,
        mapping: mapping.status === 'fulfilled' ? mapping.value.data : null,
        audit: audit.status === 'fulfilled' ? audit.value.data : [],
        unavailable: mapping.status === 'rejected' || audit.status === 'rejected' });
    } catch { /* Optional integration never blocks Fieldlink payroll. */ }
  }, [isAdmin]);
  useEffect(() => { loadXero(); }, [loadXero]);

  const saveWeekEnding = async () => {
    try {
      await api.patch('/org/me', { pay_week_end_day: jsToPyDow(endDow) });
      localStorage.setItem(WEEK_END_DOW_KEY, String(endDow));
      toast.success('Company pay-week ending day updated.');
    } catch { toast.error('Company pay-week setting could not be saved.'); }
  };

  const exportPayrollPdf = async () => {
    try {
      const we = shiftWeek(weekStart, 6);
      const res = await api.get("/payroll/week/export.pdf", {
        params: { week_start: weekStart, week_end: we, _org: user.org_id, _ts: Date.now() },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll_${weekStart}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Pay-run PDF downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  // ---- Pay-run preview / per-run PDF ----
  const [previewRun, setPreviewRun] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const openPreview = async (runId) => {
    setPreviewLoading(true);
    setPreviewRun({ id: runId, _loading: true });
    try {
      const { data } = await api.get(`/payruns/${runId}/detail`);
      setPreviewRun(data);
    } catch (e) {
      toast.error("Failed to load pay run");
      setPreviewRun(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadRunPdf = async (runId, weekStartIso) => {
    try {
      const res = await api.get(`/payruns/${runId}/export.pdf`, {
        params: { _org: user.org_id, _ts: Date.now() },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payrun_${weekStartIso || "week"}_${runId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Pay-run PDF downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const allUnpaidIds = data.workers?.flatMap((w) => w.unpaid_timesheet_ids) || [];
  const totalUnpaidHours = data.workers?.reduce((a, w) => a + w.unpaid_hours, 0) || 0;
  const totalUnpaidGross = data.workers?.reduce((a, w) => a + w.unpaid_gross, 0) || 0;
  const workersWithUnpaid = data.workers?.filter((w) => w.unpaid_hours > 0) || [];

  const openConfirmation = async (open) => {
    if (!open) { if (!processing) { confirmationRequest.current?.abort(); setNotesOpen(false); } return; }
    confirmationRequest.current?.abort();
    const controller = new AbortController(); confirmationRequest.current = controller;
    setNotesOpen(true); setConfirmation(null); setConfirmationError(''); setConfirmationLoading(true);
    try {
      const { data: preview } = await api.post('/payruns/preview', {
        week_start: data.week_start, week_end: data.week_end, timesheet_ids: allUnpaidIds,
      }, { __flOnlineOnly: true, signal: controller.signal });
      if (!controller.signal.aborted) setConfirmation(preview);
    } catch { if (!controller.signal.aborted) setConfirmationError('The processing preview could not be verified. Refresh the period and review again.'); }
    finally { if (!controller.signal.aborted) setConfirmationLoading(false); }
  };

  const reconcileProcessing = async () => {
    if (!pendingOperation || processing) return;
    setProcessing(true);
    try {
      const { data: outcome } = await api.get(`/payruns/operations/${encodeURIComponent(pendingOperation)}`, { __flSkipReadCache: true });
      if (outcome.state === 'committed') {
        toast.success(outcome.pay_run?.status === 'reopened' ? 'Original operation was committed and later reopened. Review the current period.' : 'Payroll processing is confirmed in pay-run history.');
      } else if (outcome.state !== 'failed') {
        toast.warning('Outcome remains unresolved. Do not submit a new operation; check again or contact support.');
        return;
      } else toast.warning('This operation did not commit. Refresh and review before a new attempt.');
      sessionStorage.removeItem(operationStorageKey); setPendingOperation(null); setNotesOpen(false); setConfirmation(null);
      await Promise.all([loadWeek(weekStart), loadRuns()]);
    } catch { toast.error('The operation cannot yet be reconciled. Keep this operation reference and contact support if it persists.'); }
    finally { setProcessing(false); }
  };

  const processPayRun = async () => {
    if (processing || pendingOperation || loading || loadError || !confirmation || confirmation.week_start !== data.week_start || confirmation.week_end !== data.week_end || allUnpaidIds.length === 0) return;
    let operationId;
    try {
      operationId = Array.from(window.crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
      sessionStorage.setItem(operationStorageKey, operationId);
    }
    catch { toast.error('The browser cannot retain a safe retry reference. Enable session storage before processing.'); return; }
    setPendingOperation(operationId); setProcessing(true);
    try {
      const { data: response } = await api.post('/payruns', {
        week_start: confirmation.week_start, week_end: confirmation.week_end,
        timesheet_ids: confirmation.timesheet_ids, notes: notes || null,
        operation_id: operationId, preview_fingerprint: confirmation.fingerprint,
      }, { __flOnlineOnly: true });
      const run = response;
      (['attention', 'partial', 'off'].includes(run.xero_transfer?.state) ? toast.warning : toast.success)(`Payroll processed. ${xeroTransferMessage(run)}`);
      sessionStorage.removeItem(operationStorageKey); setPendingOperation(null);
      setNotesOpen(false); setNotes(''); setConfirmation(null);
      await Promise.all([loadWeek(weekStart), loadRuns()]);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Processing outcome needs checking. Use Check processing outcome before another attempt.');
      setNotesOpen(false); setConfirmation(null);
    } finally { setProcessing(false); }
  };

// Field worker view
  if (!isOffice && user.role !== 'field') return <div className="p-6"><EmptyState title="Payroll access restricted" description="Your role does not have payroll access." /></div>;
  if (!isOffice) {
    return (
      <div data-testid="payroll-page" className="p-6 lg:p-10 max-w-5xl mx-auto">
        <PageHeader tag="/ Pay history" title="My Payslips" subtitle="Every pay run that included your hours." />
        {payslips.length === 0 ? (
          <EmptyState title="No payslips yet" description="Once the office processes your approved hours, they'll show here." />
        ) : (
          <div className="space-y-3">
            {payslips.map((s) => (
              <div key={s.id} className="hard-card p-5 flex items-center justify-between" data-testid={`payslip-${s.id}`}>
                <div>
                  <div className="label-tag">Week of {s.week_start}</div>
                  <div className="font-display font-bold text-lg uppercase mt-1">{s.hours.toFixed(2)} hours @ {NZD.format(s.hourly_rate)}/hr</div>
                  <div className="text-xs text-zinc-500 font-mono mt-1">Processed {new Date(s.created_at).toLocaleDateString("en-NZ")}</div>
                </div>
                <div className="text-right">
                  <div className="metric text-3xl font-bold">{NZD.format(s.gross)}</div>
                  <div className="label-tag text-green-700 mt-1 flex items-center justify-end gap-1"><CheckCircle2 className="w-3 h-3" /> Paid</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Office/admin view
  return (
    <div data-testid="payroll-page" className="w-full min-w-0 p-4 lg:p-6 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div><h1 className="text-2xl font-semibold tracking-tight">Payroll Centre</h1><p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Review and manage your team's payroll.</p>
          {xero && <div className="mt-2 text-xs"><Link className="text-emerald-700 dark:text-emerald-300" to="/app/integrations/xero">Xero Payroll connected · {xero.tenant || 'Connected organisation'}</Link>{xero.unavailable && <span className="block text-amber-700 dark:text-amber-300">Some integration details are unavailable. Fieldlink payroll remains available.</span>}</div>}
        </div>
        <Button variant="outline" onClick={exportPayrollPdf} disabled={loading || !!loadError} data-testid="export-payroll-pdf-button"><FileText className="w-4 h-4 mr-2" />Export period PDF</Button>
      </div>
      {isAdmin && <XeroPayrollIssuesBanner />}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3 mb-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <Button size="icon" variant="outline" disabled={processing} aria-label="Previous pay period" onClick={() => setWeekStart(shiftWeek(weekStart, -7))} data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="text-xs" data-testid="week-label"><div className="text-zinc-500 dark:text-zinc-400">Pay period</div><div className="font-semibold">{prettyDate(weekStart)} → {prettyDate(shiftWeek(weekStart, 6))}</div></div>
          <Button size="icon" variant="outline" disabled={processing} aria-label="Next pay period" onClick={() => setWeekStart(shiftWeek(weekStart, 7))} data-testid="week-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setWeekStart(weekBounds(new Date(), endDow).week_start)} disabled={processing} data-testid="week-this">Latest period</Button>
          <div className="flex items-center gap-2"><Label htmlFor="week-end-input" className="text-xs whitespace-nowrap">Week ending</Label><Input id="week-end-input" disabled={processing} type="date" value={shiftWeek(weekStart, 6)} onChange={e => { if (e.target.value) setWeekStart(isoSubtractDays(e.target.value, 6)); }} className="h-9 w-auto max-w-full text-xs" data-testid="week-end-input" /></div>
          {isAdmin && <details className="relative"><summary className="text-xs cursor-pointer px-2 py-2">Payroll settings</summary><div className="mt-2 p-3 border rounded bg-white dark:bg-zinc-900 text-xs space-y-2"><Label htmlFor="pay-week-day">Company pay week ends on</Label><select id="pay-week-day" value={endDow} onChange={e => setEndDow(Number(e.target.value))} className="block border rounded p-2 bg-white dark:bg-zinc-900">{['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, i) => <option key={day} value={i}>{day}</option>)}</select><Button size="sm" onClick={saveWeekEnding}>Save company setting</Button></div></details>}
        </div>
        <Dialog open={notesOpen} onOpenChange={openConfirmation}>
          <DialogTrigger asChild>
            <Button
              disabled={loading || !!loadError || processing || !!pendingOperation || allUnpaidIds.length === 0}
              className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white font-semibold"
              data-testid="process-payrun-button"
            >
              <Banknote className="w-4 h-4 mr-2" />
              Process pay run · {NZD.format(totalUnpaidGross)}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-tight text-2xl">Confirm pay run</DialogTitle>
              <DialogDescription>
                Processing destination: Fieldlink. Exact approved, unpaid source set, regardless of table filters: {confirmation?.workers?.length || 0} worker(s) · {Number(confirmation?.total_hours || 0).toFixed(2)} hours · {NZD.format(confirmation?.total_gross || 0)} gross. This will mark these timesheets as paid. {records.filter(t => t.status === "pending").length} pending entries are excluded; they are not approved by this action.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {confirmationLoading && <p role="status">Verifying exact processing scope…</p>}
              {confirmationError && <p role="alert">{confirmationError}</p>}
              <div className="hard-card p-3 bg-zinc-50 dark:bg-zinc-950">
                {(confirmation?.workers || []).map((w) => (
                  <div key={w.user_id} className="flex items-center justify-between py-1 text-sm">
                    <span>{w.user_name}</span>
                    <span className="font-mono">{w.hours.toFixed(2)}h · {(w.hours - w.ot_hours).toFixed(2)}h ordinary · {w.ot_hours.toFixed(2)}h OT · {NZD.format(w.gross)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-300 font-bold">
                  <span>Total</span>
                  <span className="font-mono">{Number(confirmation?.total_hours || 0).toFixed(2)}h · {NZD.format(confirmation?.total_gross || 0)}</span>
                </div>
              </div>
              <div>
                <Label className="label-tag">Notes (optional)</Label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. Weekly payroll reference" data-testid="payrun-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={processPayRun} disabled={processing || loading || !!loadError || !confirmation || confirmationLoading || !!pendingOperation} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="confirm-payrun-button">
                {processing ? "Processing..." : "Confirm & Mark Paid"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {pendingOperation && <div role="alert" className="mb-3 rounded border border-amber-300 dark:border-amber-800 p-3 text-sm"><p>A payroll operation needs reconciliation before another submission.</p><p className="text-xs break-all mt-1">Reference: {pendingOperation}</p><Button className="mt-2" variant="outline" disabled={processing} onClick={reconcileProcessing}>Check processing outcome</Button></div>}
      <p className="mb-3 text-xs text-zinc-500 dark:text-zinc-400">Processing scope: {workersWithUnpaid.length} eligible employees · {totalUnpaidHours.toFixed(2)}h · {records.filter(t => t.status === "pending").length} pending entries excluded. Includes all eligible employees in the period, regardless of table filters.</p>
      {loading ? <div role="status" className="py-4 text-sm text-zinc-500 dark:text-zinc-400">Loading payroll period…</div> : loadError ? <div role="alert" className="border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 p-4 rounded-lg text-sm">{loadError}<Button variant="outline" size="sm" className="ml-2" onClick={() => loadWeek(weekStart)}>Refresh period</Button></div> : <PayrollCentre key={weekStart} data={data} records={records} runs={runs} xero={xero} preparation={null} onPreview={openPreview} onXeroRefresh={loadXero} onDataRefresh={() => loadWeek(weekStart)} />}
      {isAdmin && xeroConnected && <details><summary className="cursor-pointer text-sm text-zinc-500">Administrator Xero diagnostics</summary><PayrollExportCentre weekStart={weekStart} /></details>}
      {/* Pay run history */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5" /> Pay run history
        </h2>
        {historyError && <p role="alert" className="text-sm text-amber-700 dark:text-amber-300 mb-3">{historyError} <button className="underline" onClick={loadRuns}>Retry history</button></p>}
        {runs.length === 0 ? (
          <div className="text-sm text-zinc-500">No pay runs available in the returned history.</div>
        ) : (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 divide-y divide-zinc-200 dark:divide-zinc-800">
            {runs.map(r => <div key={r.id} className="p-3 grid gap-3 md:grid-cols-[minmax(180px,1.5fr)_1fr_1fr_auto] items-center text-sm" data-testid={`payrun-card-${r.id}`}>
              <div className="min-w-0"><div className="font-semibold">{r.week_start} → {r.week_end}</div><div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{new Date(r.created_at).toLocaleString("en-NZ")} · {r.processed_by_name}</div>{r.xero_transfer && <p className="text-xs mt-1" role="status">Payroll processed. {xeroTransferMessage(r)}</p>}<XeroTransferDetails transfer={r.xero_transfer} />{isAdmin && ['attention', 'partial'].includes(r.xero_transfer?.state) && r.status !== 'reopened' && <Button variant="outline" data-testid={`xero-transfer-retry-${r.id}`} onClick={async () => { try { await api.post(`/xero/payroll/runs/${encodeURIComponent(r.id)}/retry`, {}, {__flOnlineOnly: true}); await loadRuns(); } catch { toast.error('Xero needs attention. Review integration diagnostics.'); } }}>Retry Xero transfer</Button>}{r.notes && <p className="text-xs text-zinc-500 dark:text-zinc-400 break-words mt-1">{r.notes}</p>}{r.status === 'reopened' && <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">Reopened by {r.reopened_by_name}: {r.reopen_reason}. Original totals shown.</p>}</div>
              <div><span className="font-medium">{r.workers?.length || 0} employees</span><div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{r.total_hours}h</div></div>
              <div><div className="font-semibold tabular-nums">{NZD.format(r.total_gross)}</div><span className={`inline-block mt-1 rounded px-2 py-0.5 text-[11px] ${r.status === 'reopened' ? 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300' : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300'}`}>{r.status === 'reopened' ? 'Reopened' : 'Processed'}</span></div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => openPreview(r.id)} data-testid={`payrun-preview-${r.id}`}><Eye className="w-3.5 h-3.5 mr-1" />View</Button>
                <Button size="sm" variant="outline" onClick={() => downloadRunPdf(r.id, r.week_start)} data-testid={`payrun-pdf-${r.id}`}><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
                {r.status !== 'reopened' && canReopenPayroll && <Button size="sm" variant="outline" data-testid={`payrun-reopen-${r.id}`} onClick={() => { setReopenRun(r); setReopenReason(''); }}>Reopen</Button>}
              </div>
            </div>)}
          </div>
        )}
      </div>

      <Dialog open={!!reopenRun} onOpenChange={(open) => { if (!open && !reopening) setReopenRun(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reopen payroll</DialogTitle>
            <DialogDescription>
              Reopen the pay run for {reopenRun?.week_start}? All its timesheets will return to pending review. Recheck and approve them before processing payroll again. The original run stays in history. This does not reverse payments or update Xero.
            </DialogDescription>
          </DialogHeader>
          <Label htmlFor="payroll-reopen-reason">Reason for reopening</Label>
          <Textarea id="payroll-reopen-reason" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} maxLength={1000} disabled={reopening} />
          <DialogFooter>
            <Button variant="outline" disabled={reopening} onClick={() => setReopenRun(null)}>Cancel</Button>
            <Button disabled={reopening || !reopenReason.trim()} onClick={reopenPayroll}>{reopening ? "Reopening…" : "Reopen payroll"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay-run detail preview */}
      <PayRunPreviewDialog
        run={previewRun}
        loading={previewLoading}
        onClose={() => setPreviewRun(null)}
        onDownload={(id, ws) => downloadRunPdf(id, ws)}
      />
    </div>
  );
}

function PayRunPreviewDialog({ run, loading, onClose, onDownload }) {
  return (
    <Dialog open={!!run} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="payrun-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">
            Pay run detail
          </DialogTitle>
          {run && !run._loading && (
            <DialogDescription>
              Week of <b>{run.week_start}</b> → <b>{run.week_end}</b> · Processed by {run.processed_by_name} on {new Date(run.created_at).toLocaleString("en-NZ")}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading || run?._loading ? (
          <div className="py-10 text-center text-sm text-zinc-500 font-mono">Loading pay run…</div>
        ) : run ? (
          <div className="space-y-5">
            {run.status === "reopened" && <p className="text-sm text-amber-700">Reopened: {run.reopen_reason}. These are the original payroll figures.</p>}
            <div className="grid grid-cols-3 gap-3">
              <div className="border-2 border-zinc-900 p-3">
                <div className="label-tag">Workers</div>
                <div className="font-display font-black text-2xl mt-1">{run.workers?.length || 0}</div>
              </div>
              <div className="border-2 border-zinc-900 p-3">
                <div className="label-tag">Total hours</div>
                <div className="font-display font-black text-2xl mt-1">{Number(run.total_hours || 0).toFixed(2)}</div>
              </div>
              <div className="border-2 border-[#FF5F15] bg-[#FF5F15] text-white p-3">
                <div className="label-tag !text-white">Gross total</div>
                <div className="font-display font-black text-2xl mt-1">{NZD.format(run.total_gross || 0)}</div>
              </div>
            </div>

            {run.notes && (
              <div className="bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 p-3 text-sm text-zinc-700 dark:text-zinc-300">
                <div className="label-tag mb-1">Notes</div>
                {run.notes}
              </div>
            )}

            <div className="space-y-4">
              {(run.workers || []).map((w) => (
                <div key={w.user_id} className="hard-card p-4" data-testid={`preview-worker-${w.user_id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-display font-bold uppercase tracking-tight text-base">{w.user_name}</div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
                        {Number(w.hours || 0).toFixed(2)} hrs · @ {NZD.format(w.hourly_rate || 0)}/hr
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="label-tag">Gross</div>
                      <div className="font-display font-black text-xl text-[#FF5F15]">{NZD.format(w.gross || 0)}</div>
                    </div>
                  </div>
                  {(w.timesheets || []).length > 0 ? (
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 dark:text-zinc-300 uppercase tracking-wider text-[10px]">
                            <th className="text-left p-2 font-bold">Date</th>
                            <th className="text-left p-2 font-bold">Job</th>
                            <th className="text-right p-2 font-bold">Hours</th>
                            <th className="text-left p-2 font-bold">Status</th>
                            <th className="text-left p-2 font-bold">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {w.timesheets.map((t) => (
                            <tr key={t.id} className="border-b border-zinc-100 last:border-b-0">
                              <td className="p-2 font-mono whitespace-nowrap">{t.date}</td>
                              <td className="p-2">
                                {(!t.entry_type || t.entry_type === "work") ? (
                                  t.job_title ? (
                                    <span className="inline-flex items-center gap-1">
                                      <Briefcase className="w-3 h-3 text-zinc-400" /> {t.job_title}
                                    </span>
                                  ) : <span className="text-zinc-400 italic">—</span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-600 text-amber-800 dark:text-amber-300 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm">
                                    {ENTRY_TYPE_LABELS[t.entry_type] || t.entry_type}
                                  </span>
                                )}
                              </td>
                              <td className="p-2 text-right font-mono">{Number(t.hours || 0).toFixed(2)}</td>
                              <td className="p-2 text-zinc-600 dark:text-zinc-400 capitalize">{t.status}</td>
                              <td className="p-2 text-zinc-600 dark:text-zinc-400 max-w-[200px] truncate" title={t.notes}>{t.notes || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="mt-3 text-xs text-zinc-400 italic">No itemised timesheets recorded.</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold" data-testid="payrun-preview-close">
            Close
          </Button>
          {run && !run._loading && (
            <Button onClick={() => onDownload(run.id, run.week_start)} className="rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase tracking-wider font-bold" data-testid="payrun-preview-download">
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
